import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';

import { AlmaLink } from '../alma-link';
import * as styles from './cost-estimator.module.css';

interface CostEstimatorProps {
	abTestId: string;
	eyebrow: string;
	footnote: string;
	illustration: {
		asset: {
			gatsbyImageData: IGatsbyImageData;
		};
	};
	primaryCta: string;
	primaryCtaAnalytics: string;
	secondaryCta: string;
	secondaryCtaAnalytics: string;
	themeColor: string;
}

interface InsuranceCategory {
	categoryTitle?: string;
	tooltip?: string;
}

interface InsuranceGroup {
	description?: string;
	label: string;
	options?: InsuranceProvider[];
	title?: string;
	tooltip?: string;
}

interface InsuranceProvider {
	id: string;
	insurerCategory?: InsuranceCategory;
	insurerSlug?: string;
	is_aip?: boolean;
	label?: string;
	title: string;
	value: string;
	[key: `${string}Category`]: InsuranceCategory | undefined;
}

const setSearchOptionsWithGroups = (
	searchField: InsuranceProvider[],
	type: string
): InsuranceGroup[] => {
	const result: InsuranceGroup[] = [];

	searchField.forEach((fieldItem: InsuranceProvider) => {
		const item = {
			...fieldItem,
			label: fieldItem.title,
		};

		const categoryKey = `${type}Category` as keyof InsuranceProvider;
		const category = fieldItem[categoryKey] as
			| InsuranceCategory
			| undefined;
		const categoryTitle = category?.categoryTitle;

		// if no category title, add item to result
		if (!categoryTitle) {
			result.push(item);
			return;
		}

		const existingCategory = result.find(
			(option) => option.label === categoryTitle
		);

		if (existingCategory?.options) {
			existingCategory.options.push(item);
			return;
		}

		// if no existing category, create new category
		const newCategory = {
			label: categoryTitle,
			tooltip: category?.tooltip,
			options: [item],
		};

		result.push(newCategory);
	});

	return result;
};

export function CostEstimator({
	abTestId,
	eyebrow,
	footnote,
	illustration,
	primaryCta,
	primaryCtaAnalytics,
	secondaryCta,
	secondaryCtaAnalytics,
	themeColor = 'white',
}: CostEstimatorProps) {
	// we can use static query here because we are not using any variables
	const data = useStaticQuery(graphql`
		query InsuranceProviders {
			allSanityInsurer(
				sort: [
					{ insurerCategory: { orderRank: ASC } }
					{ orderRank: ASC }
				]
			) {
				nodes {
					... on SanityInsurer {
						id
						insurerCategory {
							categoryTitle
							tooltip
						}
						insurerSlug
						is_aip
						title
						value
					}
				}
			}
		}
	`);

	const insuranceProviders = data.allSanityInsurer.nodes;
	const insuranceProvidersWithGroups = setSearchOptionsWithGroups(
		insuranceProviders,
		'insurer'
	);

	// state
	const [insurances] = useState<InsuranceGroup[]>(
		insuranceProvidersWithGroups
	);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [loading, setLoading] = useState(false);
	const [providerCount, setProviderCount] = useState(0);
	const [providerCountWithSlidingScale, setProviderCountWithSlidingScale] =
		useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedInsurance, setSelectedInsurance] =
		useState<InsuranceProvider | null>(null);

	// refs
	const dropdownRef = useRef<HTMLDivElement>(null);

	// used to close dropdown when clicking outside of dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// fetch data when insurance is selected to get the count of providers who accept the selected insurance
	useEffect(() => {
		const fetchProviders = async () => {
			if (!selectedInsurance) return;

			setLoading(true);

			const baseUrl =
				'https://secure.helloalma.com/api/v1/providerProfiles/search/?page=1&limit=2';

			const apiUrl = `${baseUrl}&filterables=${selectedInsurance.value}`;
			const apiUrlWithSlidingScale = `${baseUrl}&filterables=payment_sliding_scale`;

			try {
				const response = await fetch(apiUrl);
				const data = await response.json();

				if (data.count === 0) {
					const response = await fetch(apiUrlWithSlidingScale);
					const data = await response.json();

					setProviderCount(0);
					setProviderCountWithSlidingScale(data.count);
				} else {
					setProviderCount(data.count);
				}
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		void fetchProviders();
	}, [selectedInsurance]);

	const toggleDropdown = () => {
		setIsDropdownOpen((prev) => !prev);
	};

	const handleInsuranceSelect = (insurance: InsuranceProvider | null) => {
		setSelectedInsurance(insurance);
		setSearchTerm('');
		setIsSearching(false);
		toggleDropdown();
	};

	const insuranceIcon = getImage(illustration?.asset?.gatsbyImageData);

	const filterOutNonePayOutOfPocket = (groups: InsuranceGroup[]) =>
		groups
			.map((group) => {
				if (group.options) {
					return {
						...group,
						options: group.options.filter(
							(option) =>
								option.title !== `None - I'll pay out of pocket`
						),
					};
				}
				return group.title === `None - I'll pay out of pocket`
					? null
					: group;
			})
			.filter(Boolean) as InsuranceGroup[];

	const filteredInsurances =
		isSearching && searchTerm.trim() !== ''
			? filterOutNonePayOutOfPocket(
					insurances
						.map((group) => {
							if (group.options) {
								return {
									...group,
									options: group.options.filter((option) =>
										option.title
											.toLowerCase()
											.includes(searchTerm.toLowerCase())
									),
								};
							}
							return group.title
								?.toLowerCase()
								.includes(searchTerm.toLowerCase())
								? group
								: null;
						})
						.filter(Boolean) as InsuranceGroup[]
				)
			: filterOutNonePayOutOfPocket(insurances);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setIsSearching(true);
		setIsDropdownOpen(true);
	};

	const handleInputClick = () => {
		setIsDropdownOpen(true);
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setIsDropdownOpen(true);
		}
		if (e.key === 'Escape') {
			setIsDropdownOpen(false);
		}
	};

	const handleInputFocus = () => {
		setIsSearching(true);
		setIsDropdownOpen(true);
	};

	// results header and text
	const resultsHeader = providerCount === 0 ? 'Results:' : 'Great news:';
	const resultsText =
		providerCount === 0
			? `No providers in-network yet, but ${providerCountWithSlidingScale.toLocaleString()} Alma therapists offer sliding scale payment.`
			: `${providerCount.toLocaleString()} Alma therapists accept ${
					selectedInsurance?.is_aip
						? selectedInsurance.title
						: 'your insurance'
				}.`;

	// primary cta values
	const encodedSlidingScaleFilter = btoa('payment_sliding_scale');

	const primaryCtaText =
		providerCount === 0 ? 'Find sliding scale providers' : primaryCta;

	const primaryCtaLink =
		providerCount === 0
			? `https://secure.helloalma.com/providers-results/?filterables=${encodedSlidingScaleFilter}`
			: `https://secure.helloalma.com/providers-results/${selectedInsurance?.insurerSlug ? `${selectedInsurance.insurerSlug}` : ''}`;

	return (
		<div className={classNames(styles.container, styles[themeColor])}>
			<div className={styles.header}>
				<p className={classNames('body-m', styles.eyebrow)}>
					{eyebrow}
				</p>
				<div className={styles.selectContainer}>
					<div
						ref={dropdownRef}
						className={styles.customSelect}
						tabIndex={0}
					>
						<div className={styles.inputContainer}>
							<input
								type='text'
								placeholder='Who is your insurance provider?'
								value={
									isSearching
										? searchTerm
										: selectedInsurance?.title ?? ''
								}
								onChange={handleInputChange}
								onClick={handleInputClick}
								onKeyDown={handleInputKeyDown}
								onFocus={handleInputFocus}
								className={styles.searchInput}
							/>
							<div
								className={styles.chevron}
								onClick={() =>
									setIsDropdownOpen(!isDropdownOpen)
								}
							/>
						</div>
						<div
							className={classNames(
								isDropdownOpen && styles.show,
								styles.dropdown
							)}
						>
							{filteredInsurances.length === 0 ||
							filteredInsurances.every(
								(group) => group.options?.length === 0
							) ? (
								<div className={styles.noResultsContainer}>
									<p className={styles.noResultsTitle}>
										Don&apos;t see your insurance provider?
									</p>
									<p className={styles.noResultsText}>
										We&apos;re always working to add more
										payment options. Explore out-of-pocket
										options or{' '}
										<a
											href='https://secure.helloalma.com/insurance-coverage/'
											className={styles.noResultsLink}
											target='_blank'
											rel='noopener noreferrer'
										>
											learn more about paying for therapy
											without insurance coverage.
										</a>
									</p>
									<div className={styles.noResultsCtaRow}>
										<span
											className={styles.noResultsIcon}
											aria-hidden='true'
										>
											🔍
										</span>
										<a
											href='https://secure.helloalma.com/providers-results/'
											className={styles.noResultsCta}
											target='_blank'
											rel='noopener noreferrer'
										>
											Search for out-of-pocket-care
										</a>
									</div>
								</div>
							) : (
								filteredInsurances.map((group) =>
									group.options &&
									group.options.length > 0 ? (
										<div key={group.label}>
											<div className={styles.optionGroup}>
												<span>{group.label}</span>
												{group.tooltip && (
													<div
														className={
															styles.tooltipContainer
														}
													>
														<span
															className={
																styles.tooltipIcon
															}
														>
															i
														</span>
														<div
															className={
																styles.tooltip
															}
														>
															{group.tooltip}
														</div>
													</div>
												)}
											</div>
											{group.options.map((insurance) => (
												<div
													key={insurance.id}
													className={styles.option}
													onClick={() =>
														handleInsuranceSelect(
															insurance
														)
													}
												>
													{insurance.title}
												</div>
											))}
										</div>
									) : !group.options &&
									  group.title
											?.toLowerCase()
											.includes(
												searchTerm.toLowerCase()
											) ? (
										<div
											key={group.label}
											className={styles.option}
											onClick={() =>
												handleInsuranceSelect(
													group as InsuranceProvider
												)
											}
										>
											{group.title}
										</div>
									) : null
								)
							)}
						</div>
					</div>
				</div>
			</div>
			<>
				{!selectedInsurance && insuranceIcon && (
					<>
						<GatsbyImage
							alt='Insurance icon'
							className={styles.emptyImage}
							image={insuranceIcon}
						/>
						<p className={classNames('body-m', styles.footnote)}>
							{footnote}
						</p>
					</>
				)}
				{selectedInsurance && (
					<div
						className={classNames(styles.resultsContainer, {
							[styles.fade]: loading,
						})}
					>
						<div className={styles.results}>
							<p className='heading-xs'>{resultsHeader}</p>
							<p
								className={classNames(
									'heading-s',
									styles.resultsText
								)}
							>
								{resultsText}
							</p>
						</div>
						<div className={styles.ctaContainer}>
							<AlmaLink
								analytics={primaryCtaAnalytics}
								cta={{
									abTestId,
									buttonColorTheme: 'dark',
									kind: 'button',
									link: primaryCtaLink,
									title: primaryCtaText,
								}}
							/>
							{selectedInsurance?.is_aip && (
								<AlmaLink
									analytics={secondaryCtaAnalytics}
									cta={{
										abTestId,
										arrowPosition: 'right',
										hasArrow: true,
										kind: 'link',
										link: 'https://secure.helloalma.com/check-my-insurance-coverage/',
										linkColor: 'cblgr',
										title: secondaryCta,
										underlined: true,
									}}
								/>
							)}
						</div>
					</div>
				)}
			</>
		</div>
	);
}
